import React, { useMemo, useRef } from 'react';
import { Box, Typography, Tooltip, Button } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import IconButton from '@mui/material/IconButton';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import { toPng } from 'html-to-image';

import { Category } from '../../types/Category';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CloseIcon from '@mui/icons-material/Close';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import FormGroup from '@mui/material/FormGroup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Collapse from '@mui/material/Collapse';
import { useHelpContent } from '../../utils/HelpUtils';

const highlightColors = [
  '#ffe066', // yellow
  '#ffadad', // red
  '#b5ead7', // green
  '#a0c4ff', // blue
  '#ffd6a5', // orange
  '#caffbf', // light green
  '#9bf6ff', // cyan
  '#bdb2ff', // purple
];

type HorizontalNodePos = { x: number; y: number };

function buildCategoryIndex(root: Category): Map<string, Category> {
  const m = new Map<string, Category>();
  const walk = (c: Category) => {
    m.set(c.id, c);
    (c.children ?? []).forEach(walk);
  };
  walk(root);
  return m;
}

/** Selected subcases that have at least one content row in this content category. */
function buildCoveragePresent(root: Category, selectedSubcases: string[]): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  const walk = (cat: Category) => {
    if (cat.contentCategory) {
      const present = new Set<string>();
      if (selectedSubcases.length > 0 && cat.contents) {
        for (const co of cat.contents) {
          if (co.subcase && selectedSubcases.includes(co.subcase)) present.add(co.subcase);
        }
      }
      map.set(cat.id, present);
    }
    (cat.children ?? []).forEach(walk);
  };
  walk(root);
  return map;
}

/** More coverage → darker fill (content categories only when shading is on). */
function coverageFillRatio(presentCount: number, totalSelected: number): number {
  if (totalSelected <= 0) return 0;
  return Math.max(0, Math.min(1, presentCount / totalSelected));
}

function coverageBackground(ratio: number): string {
  const c0 = [255, 255, 255];
  const c1 = [70, 160, 210];
  const r = Math.round(c0[0] + (c1[0] - c0[0]) * ratio);
  const g = Math.round(c0[1] + (c1[1] - c0[1]) * ratio);
  const b = Math.round(c0[2] + (c1[2] - c0[2]) * ratio);
  return `rgb(${r},${g},${b})`;
}

const SUBCASE_LIST_MAX_SHOWN = 9;

const HorizontalSubTreeGraph: React.FC<{
  rootCategory: Category;
  isRTL: boolean;
  onLayout?: (info: { canvasW: number; rootX: number; rootY: number }) => void;
  scale?: number;
  selectedSubcases: string[];
  coverageShading: boolean;
  showSubcaseList: boolean;
}> = ({ rootCategory, isRTL, onLayout, scale = 1, selectedSubcases, coverageShading, showSubcaseList }) => {
  const NODE_W = 240;
  const NODE_MIN_W = 200;
  const H_GAP = 40;
  const V_GAP = 110;
  const MIN_NODE_H = 52;
  // p.y = vertical center of the category Paper (fixed). Subcase list hangs below and does not move the Paper.
  // Padding: room above first row’s paper top (paper top = p.y - paperH/2).
  const PAD_X = NODE_W / 2 + 24;
  const PAD_Y = MIN_NODE_H / 2 + 28;

  const { positions, edges, canvas, categoryById, coveragePresent, paperHeightById, listBelowById } =
    useMemo(() => {
    const catMap = buildCategoryIndex(rootCategory);
    const cov = buildCoveragePresent(rootCategory, selectedSubcases);

    /** Category title box only (subcase list is laid out below the Paper). */
    const TITLE_PAPER_H = 44 + 12;

    const paperHeightByIdLocal: Record<string, number> = {};
    /** Height from bottom of Paper to bottom of subcase list (mt + lines); 0 if no list. */
    const listBelowByIdLocal: Record<string, number> = {};

    const computeHeight = (cat: Category): void => {
      if (showSubcaseList && cat.contentCategory) {
        const present = cov.get(cat.id) ?? new Set();
        const ordered =
          selectedSubcases.length > 0 ? selectedSubcases.filter(s => present.has(s)) : [];
        let lineCount = 1;
        if (ordered.length > 0) {
          lineCount =
            Math.min(ordered.length, SUBCASE_LIST_MAX_SHOWN) +
            (ordered.length > SUBCASE_LIST_MAX_SHOWN ? 1 : 0);
        }
        const listBelowPaper = 6 + lineCount * 15;
        paperHeightByIdLocal[cat.id] = Math.max(MIN_NODE_H, TITLE_PAPER_H);
        listBelowByIdLocal[cat.id] = listBelowPaper;
      } else {
        const h = Math.max(MIN_NODE_H, TITLE_PAPER_H);
        paperHeightByIdLocal[cat.id] = h;
        listBelowByIdLocal[cat.id] = 0;
      }
      (cat.children ?? []).forEach(computeHeight);
    };
    computeHeight(rootCategory);

    const pos: Record<string, HorizontalNodePos> = {};
    const edgeList: Array<{ from: string; to: string }> = [];

    const subtreeWidth = (n: Category): number => {
      const kids = n.children ?? [];
      if (!kids.length) return NODE_W;
      const widths = kids.map(subtreeWidth);
      const totalKids = widths.reduce((a, b) => a + b, 0) + H_GAP * (widths.length - 1);
      return Math.max(NODE_W, totalKids);
    };

    const place = (n: Category, left: number, depth: number) => {
      const kids = n.children ?? [];
      const w = subtreeWidth(n);
      const cx = left + w / 2;
      const cy = PAD_Y + depth * V_GAP;
      pos[n.id] = { x: cx, y: cy };

      if (!kids.length) return;

      let curLeft = left + (w - (kids.map(subtreeWidth).reduce((a, b) => a + b, 0) + H_GAP * (kids.length - 1))) / 2;
      for (const ch of kids) {
        const cw = subtreeWidth(ch);
        edgeList.push({ from: n.id, to: ch.id });
        place(ch, curLeft, depth + 1);
        curLeft += cw + H_GAP;
      }
    };

    const totalW = subtreeWidth(rootCategory) + PAD_X * 2;
    place(rootCategory, PAD_X, 0);

    let maxBottom = 0;
    for (const [id, p] of Object.entries(pos)) {
      const ph = paperHeightByIdLocal[id] ?? MIN_NODE_H;
      const below = listBelowByIdLocal[id] ?? 0;
      maxBottom = Math.max(maxBottom, p.y + ph / 2 + below);
    }
    const totalH = maxBottom + PAD_Y;

    return {
      positions: pos,
      edges: edgeList,
      canvas: { w: totalW, h: totalH },
      categoryById: catMap,
      coveragePresent: cov,
      paperHeightById: paperHeightByIdLocal,
      listBelowById: listBelowByIdLocal,
    };
  }, [rootCategory, selectedSubcases, showSubcaseList]);

  React.useEffect(() => {
    if (!onLayout) return;
    const rootPos = positions[rootCategory.id];
    if (!rootPos) return;
    onLayout({ canvasW: canvas.w, rootX: rootPos.x, rootY: rootPos.y });
  }, [onLayout, positions, rootCategory.id, canvas.w]);

  const totalSelected = selectedSubcases.length;

  return (
    <Box
      sx={{
        position: 'relative',
        width: canvas.w,
        height: canvas.h,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <svg
        width={canvas.w}
        height={canvas.h}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        {edges.map((e) => {
          const a = positions[e.from];
          const b = positions[e.to];
          if (!a || !b) return null;
          const phA = paperHeightById[e.from] ?? MIN_NODE_H;
          const phB = paperHeightById[e.to] ?? MIN_NODE_H;
          const listA = listBelowById[e.from] ?? 0;
          const x1 = a.x;
          const y1 = a.y + phA / 2 + listA;
          const x2 = b.x;
          const y2 = b.y - phB / 2;
          const midY = (y1 + y2) / 2;
          const d = `M ${x1} ${y1} L ${x1} ${midY} L ${x2} ${midY} L ${x2} ${y2}`;
          return (
            <path
              key={`${e.from}-${e.to}`}
              d={d}
              fill="none"
              stroke="#cfcfcf"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        })}
      </svg>

      {Object.entries(positions).map(([id, p]) => {
        const cat = categoryById.get(id);
        const name = cat?.name ?? id;
        const isRoot = id === rootCategory.id;
        const present = cat?.contentCategory ? coveragePresent.get(id) ?? new Set<string>() : null;
        const ratio =
          coverageShading && cat?.contentCategory && totalSelected > 0 && present
            ? coverageFillRatio(present.size, totalSelected)
            : 0;
        const bg =
          coverageShading && cat?.contentCategory && totalSelected > 0
            ? coverageBackground(ratio)
            : '#fff';
        const orderedSubs =
          present && selectedSubcases.length > 0 ? selectedSubcases.filter(s => present.has(s)) : [];
        const shown = orderedSubs.slice(0, SUBCASE_LIST_MAX_SHOWN);
        const more = orderedSubs.length - shown.length;
        const covTitle =
          cat?.contentCategory && totalSelected > 0 && present
            ? `Subcase coverage: ${present.size} / ${totalSelected} selected`
            : cat?.contentCategory
              ? 'Content category'
              : '';

        const ph = paperHeightById[id] ?? MIN_NODE_H;

        return (
          <Tooltip key={id} title={covTitle} disableHoverListener={!covTitle}>
            <Box
              sx={{
                position: 'absolute',
                left: p.x,
                top: p.y - ph / 2,
                transform: 'translateX(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                width: NODE_W,
                minWidth: NODE_MIN_W,
                maxWidth: 360,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  px: 1.75,
                  py: 1,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: bg,
                  width: '100%',
                  minHeight: ph,
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isRoot ? 800 : 700,
                    wordBreak: 'break-word',
                    textAlign: 'center',
                    color: ratio > 0.55 ? '#fff' : 'inherit',
                    textShadow: ratio > 0.55 ? '0 0 2px rgba(0,0,0,0.35)' : 'none',
                  }}
                >
                  {name}
                </Typography>
              </Paper>
              {showSubcaseList && cat?.contentCategory && (
                <Box
                  sx={{
                    mt: 0.75,
                    width: '100%',
                    textAlign: isRTL ? 'right' : 'left',
                  }}
                >
                  {selectedSubcases.length === 0 ? (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      (select subcases in the dialog for coverage list)
                    </Typography>
                  ) : orderedSubs.length === 0 ? (
                    <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                      — none of the selected subcases
                    </Typography>
                  ) : (
                    <>
                      {shown.map(s => (
                        <Typography
                          key={s}
                          variant="caption"
                          sx={{
                            display: 'block',
                            lineHeight: 1.35,
                            color: 'text.secondary',
                          }}
                        >
                          • {s}
                        </Typography>
                      ))}
                      {more > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: 'text.secondary',
                            fontStyle: 'italic',
                          }}
                        >
                          +{more} more
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

function highlightText(
  text: string,
  searchWords: string[],
  caseSensitive: boolean,
  regex: RegExp,
  colorMap: { [key: string]: string },
  selectedMatch?: { matchIdx: number; wordIdx: number },
  wholeWordOnly?: boolean
) {
  if (!searchWords.length || !text) return text;
  const normalizedText = text.replace(/\n/g, ' ');
  let lastIndex = 0;
  let matchIdx = 0;
  const parts = [];
  
  if (wholeWordOnly) {
    // Custom whole word highlighting
    searchWords.forEach((searchWord, wordIdx) => {
      const searchRegex = new RegExp(searchWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), caseSensitive ? 'g' : 'gi');
      let match;
      
      while ((match = searchRegex.exec(normalizedText)) !== null) {
        const matchStart = match.index;
        const matchEnd = matchStart + match[0].length;
        
        // Check if it's a whole word by examining characters before and after
        const beforeChar = matchStart > 0 ? normalizedText[matchStart - 1] : '';
        const afterChar = matchEnd < normalizedText.length ? normalizedText[matchEnd] : '';
        
        // Check if characters before and after are not word characters
        const isWordChar = (char: string) => /[a-zA-Z0-9_\u0590-\u05FF]/.test(char);
        
        if (!isWordChar(beforeChar) && !isWordChar(afterChar)) {
          if (matchStart > lastIndex) {
            parts.push(text.slice(lastIndex, matchStart));
          }
          
          const colorKey = caseSensitive ? searchWord : searchWord.toLowerCase();
          const color = colorMap[colorKey] || '#ffe066';
          const isSelected = selectedMatch && selectedMatch.matchIdx === matchIdx && selectedMatch.wordIdx === wordIdx;
          
          parts.push(
            <span
              key={`${matchStart}-${wordIdx}`}
              style={{
                backgroundColor: color,
                border: isSelected ? '2px solid #ff9800' : undefined,
                borderRadius: isSelected ? 3 : undefined,
                boxShadow: isSelected ? '0 0 0 2px #ffe066' : undefined,
                padding: isSelected ? '0 2px' : undefined,
                position: 'relative',
                zIndex: isSelected ? 1 : 0,
              }}
            >
              {match[0]}
            </span>
          );
          
          lastIndex = matchEnd;
          matchIdx++;
        }
      }
    });
  } else {
    // Regular regex highlighting - need to check which word matched
    let match;
    while ((match = regex.exec(normalizedText)) !== null) {
      if (!match[0]) {
        regex.lastIndex++;
        continue;
      }

      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      const matchedText = match[0];
      
      // Find which word matched by checking against each search word
      let wordIdx = -1;
      for (let i = 0; i < searchWords.length; i++) {
        const searchWord = searchWords[i];
        const searchRegex = new RegExp(searchWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), caseSensitive ? 'g' : 'gi');
        if (searchRegex.test(matchedText)) {
          wordIdx = i;
          break;
        }
      }
      
      const colorKey = caseSensitive ? searchWords[wordIdx] : searchWords[wordIdx]?.toLowerCase();
      const color = colorMap[colorKey] || '#ffe066';
      const isSelected = selectedMatch && selectedMatch.matchIdx === matchIdx && selectedMatch.wordIdx === wordIdx;
      parts.push(
        <span
          key={match.index}
          style={{
            backgroundColor: color,
            border: isSelected ? '2px solid #ff9800' : undefined,
            borderRadius: isSelected ? 3 : undefined,
            boxShadow: isSelected ? '0 0 0 2px #ffe066' : undefined,
            padding: isSelected ? '0 2px' : undefined,
            position: 'relative',
            zIndex: isSelected ? 1 : 0,
          }}
        >
          {matchedText}
        </span>
      );
      lastIndex = match.index + matchedText.length;
      matchIdx++;
    }
  }
  
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

function parseSearchTerms(input: string): [string[], boolean] {
  if (!input.trim()) return [[], false];

  // Check for quoted phrases first
  const quotedPhrases: string[] = [];
  let remainingInput = input;
  
  // Extract quoted phrases (both English and Hebrew quotes)
  const quoteRegex = /["״]([^"״]+)["״]/g;
  let match;
  while ((match = quoteRegex.exec(input)) !== null) {
    quotedPhrases.push(match[1]);
    // Remove the quoted phrase from remaining input
    remainingInput = remainingInput.replace(match[0], '');
  }
  
  // Split remaining input by whitespace and filter out empty terms
  const remainingTerms = remainingInput.split(/\s+/).filter(term => term.length > 0);
  
  // Combine quoted phrases and remaining terms
  const allTerms = [...quotedPhrases, ...remainingTerms];
  
  const hasRegex = allTerms.some(term => term.includes('*') || term.includes('?') || term.includes('+') || term.includes('[') || term.includes(']') || term.includes('(') || term.includes(')') || term.includes('\\') || term.includes('^') || term.includes('$') || term.includes('|') || term.includes('{') || term.includes('}') || term.includes('.'));
  
  if (hasRegex) {
    try {
      const regex = new RegExp(input, 'g');
      return [[input], true];
    } catch {
      return [allTerms, false];
    }
  }
  
  return [allTerms, false];
}

interface SubTreeViewProps {
  rootCategory: Category;
  allCategories: Category[];
  subcases: string[];
  includeDescriptions: boolean;
  level?: number;
  sourceTexts?: { [key: string]: string[] }; // Map of content ID to array of source texts
}

function flattenTree(
  category: Category,
  level: number,
  includeDescriptions: boolean,
  subcases: string[],
  searchWords: string[],
  caseSensitive: boolean,
  regex: RegExp,
  colorMap: { [key: string]: string },
  searchFields: { category: boolean; description: boolean; subcase: boolean; content: boolean }
): Array<{
  type: 'category' | 'contentCategory' | 'description' | 'content' | 'subcase';
  content: string;
  level: number;
  id: string;
}> {
  const items: Array<{
    type: 'category' | 'contentCategory' | 'description' | 'content' | 'subcase';
    content: string;
    level: number;
    id: string;
  }> = [];

  // Add category name if it should be searched
  if (searchFields.category && category.name && category.name.trim()) {
    items.push({
      type: category.contentCategory ? 'contentCategory' : 'category',
      content: category.name,
      level,
      id: `category-${category.id}`
    });
  }

  // Add description if it should be searched and exists
  if (includeDescriptions && searchFields.description && category.description && category.description.trim()) {
    items.push({
      type: 'description',
      content: category.description,
      level: level + 1,
      id: `description-${category.id}`
    });
  }

  // Add contents if they match subcases
  if (subcases.length > 0 && category.contents) {
    category.contents
      .filter(content => subcases.includes(content.subcase))
      .forEach(content => {
        if (content.subcase && searchFields.subcase && content.subcase.trim()) {
          items.push({
            type: 'subcase',
            content: content.subcase,
            level: level + 1,
            id: `subcase-${content.id}`
          });
        }
        if (content.text && searchFields.content && content.text.trim()) {
          items.push({
            type: 'content',
            content: content.text,
            level: level + 1,
            id: `content-${content.id}`
          });
        }
      });
  }

  // Recursively add children
  if (category.children) {
    category.children.forEach(child => {
      items.push(...flattenTree(
        child,
        level + 1,
        includeDescriptions,
        subcases,
        searchWords,
        caseSensitive,
        regex,
        colorMap,
        searchFields
      ));
    });
  }

  return items;
}

const MAX_HISTORY = 5;

const SubTreeView: React.FC<SubTreeViewProps> = ({
  rootCategory,
  allCategories,
  subcases,
  includeDescriptions,
  level = 0,
  sourceTexts
}) => {
  const [currentMatch, setCurrentMatch] = React.useState(0);
  const [itemHeights, setItemHeights] = React.useState<{ [key: string]: number }>({});
  const contentRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>({});
  const virtuosoRef = React.useRef<VirtuosoHandle>(null);

  const [search, setSearch] = React.useState('');
  const [caseSensitive, setCaseSensitive] = React.useState(false);
  const [wholeWordOnly, setWholeWordOnly] = React.useState(false);
  const [searchHistory, setSearchHistory] = React.useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('subtree_search_history');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [searchBoxAnchor, setSearchBoxAnchor] = React.useState<null | HTMLElement>(null);

  // Search fields state
  const [searchFields, setSearchFields] = React.useState({
    category: true,
    description: true,
    subcase: true,
    content: true,
  });

  // State for expanded source texts
  const [expandedSources, setExpandedSources] = React.useState<{ [key: string]: boolean }>({});
  const [showSourceTexts, setShowSourceTexts] = React.useState(false);
  const [layoutMode, setLayoutMode] = React.useState<'vertical' | 'horizontal'>(() => {
    try {
      const saved = localStorage.getItem('subtree_layout_mode');
      return saved === 'horizontal' ? 'horizontal' : 'vertical';
    } catch {
      return 'vertical';
    }
  });
  const [horizontalZoom, setHorizontalZoom] = React.useState<number>(() => {
    try {
      const saved = localStorage.getItem('subtree_horizontal_zoom');
      const z = saved ? Number(saved) : 1;
      return Number.isFinite(z) && z > 0 ? z : 1;
    } catch {
      return 1;
    }
  });
  const horizontalScrollRef = React.useRef<HTMLDivElement | null>(null);
  const horizontalLayoutRef = React.useRef<{ canvasW: number; rootX: number; rootY: number } | null>(null);
  const horizontalExportRef = React.useRef<HTMLDivElement | null>(null);
  const [exportingHorizontalPng, setExportingHorizontalPng] = React.useState(false);
  const horizontalDragRef = React.useRef<{
    isDragging: boolean;
    startX: number;
    startY: number;
    startScrollLeft: number;
    startScrollTop: number;
  }>({
    isDragging: false,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    startScrollTop: 0,
  });

  const [horizontalCoverageShading, setHorizontalCoverageShading] = React.useState(() => {
    try {
      return localStorage.getItem('subtree_horizontal_coverage') === '1';
    } catch {
      return false;
    }
  });
  const [horizontalShowSubcaseList, setHorizontalShowSubcaseList] = React.useState(() => {
    try {
      return localStorage.getItem('subtree_horizontal_subcase_list') === '1';
    } catch {
      return false;
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem('subtree_horizontal_coverage', horizontalCoverageShading ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [horizontalCoverageShading]);

  React.useEffect(() => {
    try {
      localStorage.setItem('subtree_horizontal_subcase_list', horizontalShowSubcaseList ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [horizontalShowSubcaseList]);

  const { getTooltipTextForState } = useHelpContent();

  const makeSafeFilename = (name: string) =>
    (name || 'subtree')
      .replace(/[<>:"/\\|?*]/g, '-') // Windows filename invalid characters
      .replace(/\s+/g, '-') // spaces -> hyphens
      .replace(/-+/g, '-') // collapse
      .replace(/^-|-$/g, ''); // trim hyphens

  // Save history to localStorage
  React.useEffect(() => {
    localStorage.setItem('subtree_search_history', JSON.stringify(searchHistory));
  }, [searchHistory]);

  // Print functionality
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = generatePrintContent();
    const isRTL = /[\u0590-\u05FF]/.test(rootCategory.name);
    const rtlDir = isRTL ? 'dir="rtl"' : '';
    const rtlBodyStyle = isRTL ? 'direction: rtl; text-align: right;' : '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html ${rtlDir}>
        <head>
          <title>Subtree View - ${rootCategory.name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; ${rtlBodyStyle} }
            .category { margin: 10px 0; }
            .content { margin: 5px 0; }
            .description { margin: 5px 0; font-style: italic; color: #666; }
            .level-0 { font-weight: bold; font-size: 18px; }
            .level-1 { font-weight: bold; font-size: 16px; }
            .level-2 { font-weight: bold; font-size: 14px; }
            .level-3 { font-weight: bold; font-size: 12px; }
            .level-4 { font-weight: bold; font-size: 12px; }
            .level-5 { font-weight: bold; font-size: 12px; }
            .content-category { color: #87CEEB; }
            .regular-category { color: #1976d2; }
            .content-text { margin: 5px 0; }
            .search-highlight { background-color: #ffe066; }
            @media print { body { margin: 10px; } }
          </style>
        </head>
        <body>
          <h1>Subtree View: ${rootCategory.name}</h1>
          <p><strong>Generated on:</strong> ${new Date().toLocaleString()}</p>
          ${search ? `<p><strong>Search:</strong> "${search}"</p>` : ''}
          <div class="tree-content">${printContent}</div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleExportDocx = async () => {
    const printItems = flattenTree(
      rootCategory,
      0,
      includeDescriptions,
      subcases,
      searchWords,
      caseSensitive,
      regex,
      colorMap,
      searchFields
    );

    const isRTL = /[\u0590-\u05FF]/.test(rootCategory.name);
    
    const children = [
      new Paragraph({
        text: `Subtree View: ${rootCategory.name}`,
        heading: HeadingLevel.HEADING_1,
        alignment: isRTL ? AlignmentType.RIGHT : AlignmentType.LEFT,
      }),
      new Paragraph({
        text: `Generated on: ${new Date().toLocaleString()}`,
        alignment: isRTL ? AlignmentType.RIGHT : AlignmentType.LEFT,
      }),
    ];

    if (search) {
      children.push(
        new Paragraph({
          text: `Search: "${search}"`,
          alignment: isRTL ? AlignmentType.RIGHT : AlignmentType.LEFT,
        })
      );
    }

    children.push(new Paragraph({ text: '' })); // Empty line

    printItems
      .filter(item => item.content && item.content.trim() !== '')
      .forEach(item => {
        let text = '';
        let icon = '';

        switch (item.type) {
          case 'category':
          case 'contentCategory':
            icon = item.type === 'contentCategory' ? '📄' : '📁';
            text = isRTL ? `${item.content} ${icon}` : `${icon} ${item.content}`;
            break;
          case 'subcase':
            icon = '📝';
            text = isRTL ? `${item.content} ${icon}` : `${icon} ${item.content}`;
            break;
          case 'content':
            text = item.content;
            break;
          case 'description':
            icon = '💬';
            text = isRTL ? `${item.content} ${icon}` : `${icon} ${item.content}`;
            break;
        }

        if (text) {
          // Calculate indentation based on level
          const indent = item.level * 720; // 720 twips = 0.5 inches
          
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: text,
                  size: item.level === 0 ? 32 : item.level === 1 ? 28 : 24,
                  bold: item.type === 'category' || item.type === 'contentCategory',
                  italics: item.type === 'description',
                }),
              ],
              alignment: isRTL ? AlignmentType.RIGHT : AlignmentType.LEFT,
              indent: {
                left: isRTL ? 0 : indent,
                right: isRTL ? indent : 0,
              },
            })
          );
        }
      });

    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 1440,
                right: 1440,
                bottom: 1440,
                left: 1440,
              },
            },
          },
          children: children,
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // Create a safe filename that preserves Hebrew and other Unicode characters
    const safeName = makeSafeFilename(rootCategory.name);
    
    link.download = `subtree-view-${safeName}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportHorizontalPng = async () => {
    const node = horizontalExportRef.current;
    if (!node) return;

    try {
      setExportingHorizontalPng(true);
      const safeName = makeSafeFilename(rootCategory.name);

      // Capture the full horizontal graph container (not the scroll viewport).
      // Use higher pixelRatio for crisp output when pasting into Word/Google Docs.
      const dataUrl = await toPng(node, {
        cacheBust: true,
        backgroundColor: '#ffffff',
        pixelRatio: Math.max(2, Math.round((window.devicePixelRatio || 1) * 2)),
      });

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `subtree-horizontal-${safeName || 'export'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Failed to export horizontal subtree PNG:', e);
    } finally {
      setExportingHorizontalPng(false);
    }
  };

  const generatePrintContent = () => {
    const printItems = flattenTree(
      rootCategory,
      0,
      includeDescriptions,
      subcases,
      searchWords,
      caseSensitive,
      regex,
      colorMap,
      searchFields
    );

    const isRTL = /[\u0590-\u05FF]/.test(rootCategory.name);
    const rtlStyle = isRTL ? 'direction: rtl; text-align: right;' : '';
    const rtlPadding = isRTL ? 'padding-right' : 'padding-left';

    return printItems
      .filter(item => item.content && item.content.trim() !== '')
      .map(item => {
        const levelClass = `level-${Math.min(item.level, 5)}`;
        const paddingValue = item.level * 20;
        
        switch (item.type) {
          case 'category':
          case 'contentCategory': {
            const categoryClass = item.type === 'contentCategory' ? 'content-category' : 'regular-category';
            const icon = item.type === 'contentCategory' ? '📄' : '📁';
            return `<div class="category ${levelClass} ${categoryClass}" style="${rtlStyle} ${rtlPadding}: ${paddingValue}px;">${icon} ${item.content}</div>`;
          }
                  case 'subcase': {
          return `<div class="subcase-text" style="${rtlStyle} ${rtlPadding}: ${paddingValue}px;">📝 ${item.content}</div>`;
        }
        case 'content': {
          return `<div class="content-text" style="${rtlStyle} ${rtlPadding}: ${paddingValue}px;">${item.content}</div>`;
        }
          case 'description': {
            return `<div class="description" style="${rtlStyle} ${rtlPadding}: ${paddingValue}px;">💬 ${item.content}</div>`;
          }
          default:
            return '';
        }
      })
      .filter(item => item.trim() !== '')
      .join('');
  };

  // Add to history when search is submitted (on Enter or blur)
  const addToHistory = (value: string) => {
    if (!value.trim()) return;
    setSearchHistory(prev => {
      const filtered = prev.filter(item => item !== value);
      return [value, ...filtered].slice(0, MAX_HISTORY);
    });
  };

  // Handle search box events
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };
  const handleSearchKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      addToHistory(search);
    }
  };
  const handleSearchBlur = () => {
    addToHistory(search);
  };
  const handleSearchFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setSearchBoxAnchor(e.currentTarget);
  };
  const handleHistoryClick = (item: string) => {
    setSearch(item);
    setSearchBoxAnchor(null);
  };

  // Parse search terms and create regex
  const [searchWords, isRegex] = useMemo(() => parseSearchTerms(search), [search]);
  const regex = useMemo(() => {
    if (isRegex) {
      try {
        return new RegExp(searchWords[0], caseSensitive ? 'g' : 'gi');
      } catch {
        return new RegExp('', 'g');
      }
    }
    
    if (!searchWords.length) return new RegExp('', 'g');
    
    if (wholeWordOnly) {
      // For whole word search, we'll use a custom approach
              const patterns = searchWords.map(word => {
          const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // Use word boundaries that work with Unicode
          const pattern = `\\b${escaped}\\b`;
          return pattern;
        });
        
        const pattern = patterns.join('|');
      return new RegExp(pattern, caseSensitive ? 'g' : 'gi');
    } else {
      // Regular search without whole word
      const patterns = searchWords.map(word => {
        const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return escaped;
      });
      
      const pattern = patterns.join('|');
      return new RegExp(pattern, caseSensitive ? 'g' : 'gi');
    }
  }, [searchWords, isRegex, caseSensitive, wholeWordOnly]);

  // Create color map for highlighting
  const colorMap = useMemo(() => {
    const map: { [key: string]: string } = {};
    searchWords.forEach((word, index) => {
      const key = caseSensitive ? word : word.toLowerCase();
      map[key] = highlightColors[index % highlightColors.length];
    });
    return map;
  }, [searchWords, caseSensitive]);

  // Flatten tree for display
  const items = useMemo(() => {
    return flattenTree(
      rootCategory,
      0,
      includeDescriptions,
      subcases,
      searchWords,
      caseSensitive,
      regex,
      colorMap,
      searchFields
    );
  }, [rootCategory, includeDescriptions, subcases, searchWords, caseSensitive, regex, colorMap, searchFields]);

  // Find all matches for navigation
  const allMatches = useMemo(() => {
    const matches: Array<{ itemIdx: number; matchIdx: number; wordIdx: number }> = [];
    
    items.forEach((item, itemIdx) => {
      if (!item.content) return;
      
      const normalizedText = item.content.replace(/\n/g, ' ');
      
      if (wholeWordOnly) {
        // Custom whole word search for Unicode support
        searchWords.forEach((searchWord, wordIdx) => {
          const searchRegex = new RegExp(searchWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), caseSensitive ? 'g' : 'gi');
          let match;
          let matchIdx = 0;
          
          while ((match = searchRegex.exec(normalizedText)) !== null) {
            const matchStart = match.index;
            const matchEnd = matchStart + match[0].length;
            
            // Check if it's a whole word by examining characters before and after
            const beforeChar = matchStart > 0 ? normalizedText[matchStart - 1] : '';
            const afterChar = matchEnd < normalizedText.length ? normalizedText[matchEnd] : '';
            
            // Check if characters before and after are not word characters
            const isWordChar = (char: string) => /[a-zA-Z0-9_\u0590-\u05FF]/.test(char);
            
            if (!isWordChar(beforeChar) && !isWordChar(afterChar)) {
              matches.push({ itemIdx, matchIdx, wordIdx });
              matchIdx++;
            }
          }
        });
      } else {
        // Regular regex search
        let match;
        let matchIdx = 0;
        
        while ((match = regex.exec(normalizedText)) !== null) {
          if (!match[0]) {
            regex.lastIndex++;
            continue;
          }
          
          let wordIdx = -1;
          for (let i = 1; i < match.length; i++) {
            if (match[i] !== undefined) {
              wordIdx = i - 1;
              break;
            }
          }
          
          matches.push({ itemIdx, matchIdx, wordIdx });
          matchIdx++;
        }
      }
    });
    
    return matches;
  }, [items, regex, wholeWordOnly, searchWords, caseSensitive]);

  function getSelectedMatchForItem(index: number) {
    const match = allMatches.find(m => m.itemIdx === index);
    return match ? { matchIdx: match.matchIdx, wordIdx: match.wordIdx } : undefined;
  }

  const getItemHeight = (index: number) => {
    const item = items[index];
    if (!item) return 50;
    
    const baseHeight = 40;
    const contentHeight = Math.ceil(item.content.length / 80) * 20;
    return Math.max(baseHeight, contentHeight);
  };

  // Render function for each item
  const renderItem = (item: any, index: number) => {
    if (!item.content) return null;
    const paddingStyle = isRTL
      ? { paddingRight: item.level * 4 }
      : { paddingLeft: item.level * 4 };
    const selectedMatch = getSelectedMatchForItem(index);
    switch (item.type) {
      case 'category':
      case 'contentCategory': {
        const highlighted = highlightText(item.content, searchWords, caseSensitive, regex, colorMap, selectedMatch, wholeWordOnly);
        // Find the category in allCategories to check if it's a content category
        const isContentCategory = item.type === 'contentCategory';

        // Use DescriptionIcon for content categories, FolderIcon for regular categories
        const CategoryIcon = isContentCategory ? DescriptionIcon : FolderIcon;
        const iconColor = isContentCategory ? '#87CEEB' : '#1976d2';

        return (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 1,
              px: 2,
              borderBottom: '1px solid #f0f0f0',
              minHeight: getItemHeight(index),
              ...paddingStyle,
            }}
          >
            <CategoryIcon sx={{ color: iconColor, mr: 1, fontSize: 20 }} />
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                wordBreak: 'break-word',
                flex: 1,
                color: iconColor,
              }}
            >
              {highlighted}
            </Typography>
          </Box>
        );
      }
      case 'subcase': {
        const highlighted = highlightText(item.content, searchWords, caseSensitive, regex, colorMap, selectedMatch, wholeWordOnly);
        const iconMargin = isRTL ? { ml: 1 } : { mr: 1 };
        return (
          <Box
            key={item.id}
            sx={{
              py: 1,
              px: 2,
              borderBottom: '1px solid #f0f0f0',
              minHeight: getItemHeight(index),
              ...paddingStyle,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', direction: isRTL ? 'rtl' : 'ltr' }}>
              <StickyNote2OutlinedIcon sx={{ color: '#ff9800', fontSize: 16, flexShrink: 0, ...iconMargin }} />
              <Typography
                variant="body2"
                sx={{
                  wordBreak: 'break-word',
                  flex: 1,
                  color: '#333',
                  lineHeight: 1.4,
                }}
              >
                {highlighted}
              </Typography>
            </Box>
          </Box>
        );
      }
      case 'content': {
        const highlighted = highlightText(item.content, searchWords, caseSensitive, regex, colorMap, selectedMatch, wholeWordOnly);
        return (
          <Box
            key={item.id}
            sx={{
              py: 1,
              px: 2,
              borderBottom: '1px solid #f0f0f0',
              minHeight: getItemHeight(index),
              ...paddingStyle,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                wordBreak: 'break-word',
                flex: 1,
                color: '#333',
                lineHeight: 1.4,
              }}
            >
              {highlighted}
            </Typography>
          </Box>
        );
      }
      case 'description': {
        const highlighted = highlightText(item.content, searchWords, caseSensitive, regex, colorMap, selectedMatch, wholeWordOnly);
        return (
          <Box
            key={item.id}
            sx={{
              py: 1,
              px: 2,
              borderBottom: '1px solid #f0f0f0',
              minHeight: getItemHeight(index),
              ...paddingStyle,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontStyle: 'italic',
                color: '#666',
                wordBreak: 'break-word',
                lineHeight: 1.4,
              }}
            >
              {highlighted}
            </Typography>
          </Box>
        );
      }
      default:
        return null;
    }
  };

  // Search match navigation UI
  React.useEffect(() => {
    if (allMatches.length && virtuosoRef.current) {
      const idx = allMatches[currentMatch]?.itemIdx;
      console.log('Scrolling to index:', idx);
      setTimeout(() => {
        virtuosoRef.current?.scrollToIndex({
          index: idx,
          align: 'start',
          behavior: 'auto',
        });
      }, 50);
    }
  }, [currentMatch, allMatches]);

  const isRTL = /[\u0590-\u05FF]/.test(rootCategory.name);

  React.useEffect(() => {
    try {
      localStorage.setItem('subtree_horizontal_zoom', String(horizontalZoom));
    } catch {
      // ignore
    }
  }, [horizontalZoom]);

  const recenterHorizontalView = React.useCallback(() => {
    if (layoutMode !== 'horizontal') return;
    const scroller = horizontalScrollRef.current;
    const info = horizontalLayoutRef.current;
    if (!scroller || !info) return;

    const zx = info.rootX * horizontalZoom;
    const zy = info.rootY * horizontalZoom;

    const targetX = Math.max(0, zx - scroller.clientWidth / 2);
    const targetY = Math.max(0, zy - scroller.clientHeight / 2);

    const apply = () => {
      const beforeX = scroller.scrollLeft;
      scroller.scrollLeft = targetX;
      scroller.scrollTop = targetY;
      if (isRTL && scroller.scrollLeft === beforeX && scroller.scrollWidth > scroller.clientWidth) {
        scroller.scrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth - targetX);
      }
    };

    // Let layout settle
    window.setTimeout(apply, 0);
  }, [horizontalZoom, isRTL, layoutMode]);

  React.useEffect(() => {
    if (layoutMode !== 'horizontal') return;
    const scroller = horizontalScrollRef.current;
    const info = horizontalLayoutRef.current;
    if (!scroller || !info) return;

    // center root horizontally in the scroll container
    const target = Math.max(0, info.rootX * horizontalZoom - scroller.clientWidth / 2);

    // Different browsers handle RTL scrollLeft differently. Try LTR-style first,
    // and if it doesn't move, try the mirrored value.
    const apply = () => {
      const before = scroller.scrollLeft;
      scroller.scrollLeft = target;
      if (isRTL && scroller.scrollLeft === before && scroller.scrollWidth > scroller.clientWidth) {
        scroller.scrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth - target);
      }
    };

    // Let layout settle (fonts + DOM)
    const t = window.setTimeout(apply, 0);
    return () => window.clearTimeout(t);
  }, [layoutMode, rootCategory.id, isRTL, horizontalZoom, horizontalShowSubcaseList, subcases]);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Sub Tree View
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {layoutMode === 'horizontal' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Export PNG (paste into Word/Google Docs)">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportHorizontalPng}
                    size="small"
                    disabled={exportingHorizontalPng}
                    sx={{ mr: 1 }}
                  >
                    {exportingHorizontalPng ? 'Exporting…' : 'Export PNG'}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Recenter view">
                <span>
                  <IconButton size="small" onClick={recenterHorizontalView}>
                    <CenterFocusStrongIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Zoom out">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setHorizontalZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))}
                    disabled={horizontalZoom <= 0.5}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="body2" sx={{ minWidth: 54, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(horizontalZoom * 100)}%
              </Typography>
              <Tooltip title="Zoom in">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setHorizontalZoom((z) => Math.min(2.0, Math.round((z + 0.1) * 10) / 10))}
                    disabled={horizontalZoom >= 2.0}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reset zoom">
                <IconButton size="small" onClick={() => setHorizontalZoom(1)}>
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <FormGroup row sx={{ ml: 1, gap: 0.5, alignItems: 'center' }}>
                <Tooltip title="Shade each content category by how many of the selected subcases have content there (darker = more coverage).">
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={horizontalCoverageShading}
                        onChange={e => setHorizontalCoverageShading(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Coverage shading</Typography>}
                    sx={{ mr: 0 }}
                  />
                </Tooltip>
                <Tooltip title="List selected subcases that have content under each content category.">
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={horizontalShowSubcaseList}
                        onChange={e => setHorizontalShowSubcaseList(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Subcase list</Typography>}
                    sx={{ mr: 0 }}
                  />
                </Tooltip>
              </FormGroup>
            </Box>
          )}
          <ToggleButtonGroup
            size="small"
            value={layoutMode}
            exclusive
            onChange={(_, v) => {
              if (!v) return;
              setLayoutMode(v);
              try {
                localStorage.setItem('subtree_layout_mode', v);
              } catch {
                // ignore
              }
            }}
          >
            <ToggleButton value="vertical">Vertical</ToggleButton>
            <ToggleButton value="horizontal">Horizontal</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      {layoutMode === 'vertical' && (
        <FormGroup row sx={{ gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={getTooltipTextForState('subtree-search-category', true)}>
            <FormControlLabel
              control={<Checkbox checked={searchFields.category} onChange={e => setSearchFields(prev => ({ ...prev, category: e.target.checked }))} />}
              label="Category names"
            />
          </Tooltip>

          <Tooltip title={getTooltipTextForState('subtree-search-description', true)}>
            <FormControlLabel
              control={<Checkbox checked={searchFields.description} onChange={e => setSearchFields(prev => ({ ...prev, description: e.target.checked }))} />}
              label="Descriptions"
            />
          </Tooltip>
          <Tooltip title={getTooltipTextForState('subtree-search-subcase', true)}>
            <FormControlLabel
              control={<Checkbox checked={searchFields.subcase} onChange={e => setSearchFields(prev => ({ ...prev, subcase: e.target.checked }))} />}
              label="Subcases"
            />
          </Tooltip>
          <Tooltip title={getTooltipTextForState('subtree-search-content', true)}>
            <FormControlLabel
              control={<Checkbox checked={searchFields.content} onChange={e => setSearchFields(prev => ({ ...prev, content: e.target.checked }))} />}
              label="Content text"
            />
          </Tooltip>
          {sourceTexts && (
            <Tooltip title={getTooltipTextForState('subtree-show-sources', true)}>
              <FormControlLabel
                control={<Checkbox checked={showSourceTexts} onChange={e => setShowSourceTexts(e.target.checked)} />}
                label="Show source texts"
              />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            size="small"
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportDocx}
            size="small"
          >
            Export DOCX
          </Button>
        </Box>
      </FormGroup>
      )}

      {layoutMode === 'vertical' && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Tooltip title={getTooltipTextForState('subtree-search-input', true)}>
          <TextField
            value={search}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchBlur}
            onFocus={handleSearchFocus}
            placeholder='Search (multiple words or "phrases")'
            size='small'
            sx={{ minWidth: 320 }}
            InputProps={{
              endAdornment: (
                search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearch('')}
                      aria-label="Clear search"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              )
            }}
          />
        </Tooltip>
        <Popper open={!!searchBoxAnchor && !search && searchHistory.length > 0} anchorEl={searchBoxAnchor} placement="bottom-start" style={{ zIndex: 1301 }}>
          <Paper sx={{ width: 320 }}>
            <List dense>
              {searchHistory.map((item, idx) => (
                <ListItemButton key={item + idx} onMouseDown={() => handleHistoryClick(item)}>
                  <ListItemText primary={item} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
        <FormGroup row sx={{ gap: 1 }}>
          <Tooltip title={getTooltipTextForState('subtree-case-sensitive', true)}>
            <FormControlLabel
              control={<Checkbox checked={caseSensitive} onChange={e => setCaseSensitive(e.target.checked)} />}
              label="Case sensitive"
            />
          </Tooltip>
          <Tooltip title={getTooltipTextForState('subtree-whole-word', true)}>
            <FormControlLabel
              control={<Checkbox checked={wholeWordOnly} onChange={e => setWholeWordOnly(e.target.checked)} />}
              label="Whole word only"
            />
          </Tooltip>
        </FormGroup>
      </Box>
      )}

      {layoutMode === 'vertical' && search && allMatches.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 600 }}>
            {currentMatch + 1} / {allMatches.length}
          </Typography>
          <Tooltip title={getTooltipTextForState('subtree-previous-match', true)}>
            <IconButton
              size="small"
              onClick={() => setCurrentMatch((prev) => (prev - 1 + allMatches.length) % allMatches.length)}
              disabled={allMatches.length === 0}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={getTooltipTextForState('subtree-next-match', true)}>
            <IconButton
              size="small"
              onClick={() => setCurrentMatch((prev) => (prev + 1) % allMatches.length)}
              disabled={allMatches.length === 0}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {layoutMode === 'vertical' ? (
        <Virtuoso
          ref={virtuosoRef}
          style={{ height: 800, width: '100%' }}
          totalCount={items.length}
          itemContent={(index: number) => renderItem(items[index], index)}
          computeItemKey={(index) => items[index].id}
          components={{
            Scroller: React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>((props, ref) => (
              <div {...props} ref={ref} dir={isRTL ? 'rtl' : 'ltr'} />
            ))
          }}
        />
      ) : (
        <Box
          ref={horizontalScrollRef}
          sx={{
            height: 800,
            width: '100%',
            overflowX: 'auto',
            overflowY: 'auto',
            p: 1,
            cursor: 'grab',
            userSelect: 'none',
          }}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            const scroller = horizontalScrollRef.current;
            if (!scroller) return;

            // Don't start panning when interacting with a button/link/control inside.
            const target = e.target as HTMLElement | null;
            if (target && target.closest('button, [role="button"], a, input, textarea, select, [contenteditable="true"]')) {
              return;
            }

            horizontalDragRef.current = {
              isDragging: true,
              startX: e.clientX,
              startY: e.clientY,
              startScrollLeft: scroller.scrollLeft,
              startScrollTop: scroller.scrollTop,
            };
            try {
              (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
            } catch {
              // ignore
            }
            scroller.style.cursor = 'grabbing';
          }}
          onPointerMove={(e) => {
            const scroller = horizontalScrollRef.current;
            if (!scroller) return;
            const state = horizontalDragRef.current;
            if (!state.isDragging) return;
            e.preventDefault();
            const dx = e.clientX - state.startX;
            const dy = e.clientY - state.startY;
            scroller.scrollLeft = state.startScrollLeft - dx;
            scroller.scrollTop = state.startScrollTop - dy;
          }}
          onPointerUp={() => {
            const scroller = horizontalScrollRef.current;
            horizontalDragRef.current.isDragging = false;
            if (scroller) scroller.style.cursor = 'grab';
          }}
          onPointerCancel={() => {
            const scroller = horizontalScrollRef.current;
            horizontalDragRef.current.isDragging = false;
            if (scroller) scroller.style.cursor = 'grab';
          }}
          onPointerLeave={() => {
            const scroller = horizontalScrollRef.current;
            horizontalDragRef.current.isDragging = false;
            if (scroller) scroller.style.cursor = 'grab';
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Horizontal view shows the category tree. Use &quot;Coverage shading&quot; to compare selected subcases across
            content categories, and &quot;Subcase list&quot; to see which selected subcases appear under each category.
          </Typography>
          <Box
            ref={horizontalExportRef}
            sx={{
              width: 'max-content',
              height: 'max-content',
              backgroundColor: '#fff',
            }}
          >
            <HorizontalSubTreeGraph
              rootCategory={rootCategory}
              isRTL={isRTL}
              scale={horizontalZoom}
              selectedSubcases={subcases}
              coverageShading={horizontalCoverageShading}
              showSubcaseList={horizontalShowSubcaseList}
              onLayout={(info) => {
                horizontalLayoutRef.current = info;
              }}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default SubTreeView;